<script setup lang="ts">
  import { onMounted, ref, computed } from 'vue'
  import { getInitials, getColorForString } from '@/lib/utils'
  import { useUserStore } from '@/store/admin/user'

  const userStore = useUserStore()
  const user = computed(() => userStore.user)
  const initials = computed(() => {
    return getInitials(user.value?.first_name + ' ' + user.value?.last_name)
  })

  const avatarColor = computed(() => {
    return `bg-${getColorForString(user.value?.first_name + ' ' + user.value?.last_name)}-600`
  })
</script>
<template>
  <header
    v-if="user"
    class="fixed inset-x-0 top-0 z-50 flex flex-wrap border-b border-gray-200 bg-white md:flex-nowrap
      md:justify-start lg:ms-[260px]"
  >
    <div
      class="flex w-full basis-full items-center justify-between px-2 py-2.5 sm:px-5 xl:grid xl:grid-cols-3"
      aria-label="Global"
    >
      <div class="flex items-center md:gap-x-3 xl:col-span-1">
        <div class="lg:hidden">
          <!-- Sidebar Toggle -->
          <button
            class="inline-flex h-[38px] w-7 items-center justify-center gap-x-2 rounded-lg border border-gray-200
              bg-white text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-100
              focus:outline-none disabled:pointer-events-none disabled:opacity-50"
            type="button"
            aria-haspopup="dialog"
            aria-expanded="false"
            data-hs-overlay="#admin-sidebar"
            aria-controls="admin-sidebar"
            aria-label="Toggle navigation"
          >
            <svg
              class="size-4 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
          <!-- End Sidebar Toggle -->
        </div>

        <div class="hidden min-w-80 lg:block xl:w-full">
          <!-- Search Input -->
          <div class="relative">
            <div
              class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5"
            >
              <svg
                class="size-4 flex-shrink-0 text-gray-400 dark:text-white/60"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              class="block w-full rounded-lg border-gray-200 bg-white py-2 pe-16 ps-10 text-sm focus:border-gray-200
                focus:outline-none focus:ring-0 disabled:pointer-events-none disabled:opacity-50"
              type="text"
              placeholder="Search Admin"
              data-hs-overlay="#admin-search"
            />
            <div
              class="pointer-events-none absolute inset-y-0 end-0 z-20 flex items-center pe-3 text-gray-400"
            >
              <svg
                class="size-3 flex-shrink-0 text-gray-400 dark:text-white/60"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
              </svg>
              <span class="mx-1">
                <svg
                  class="size-3 flex-shrink-0 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="M12 5v14" />
                </svg>
              </span>
              <span class="text-xs">/</span>
            </div>
          </div>
          <!-- End Search Input -->
        </div>
      </div>

      <div class="flex items-center justify-end gap-x-2 xl:col-span-2">
        <div class="flex items-center">
          <div class="lg:hidden">
            <!-- Search Button Icon -->
            <button
              class="inline-flex size-[38px] flex-shrink-0 items-center justify-center gap-x-2 rounded-full text-gray-500
                hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              type="button"
              data-hs-overlay="#admin-search"
            >
              <svg
                class="size-4 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </button>
            <!-- End Search Button Icon -->
          </div>
        </div>
        <div class="mx-1.5 h-6 w-px border-e border-gray-200"></div>
        <div
          v-if="user"
          class="h-[38px]"
        >
          <!-- Account Dropdown -->
          <div
            class="hs-dropdown relative inline-flex [--auto-close:inside] [--placement:bottom-right]
              [--strategy:absolute]"
          >
            <button
              id="@@id"
              class="inline-flex flex-shrink-0 items-center gap-x-3 rounded-full text-start focus:bg-gray-100
                focus:outline-none"
              type="button"
            >
              <img
                v-if="user.avatar"
                class="size-[38px] flex-shrink-0 rounded-full"
                :src="user.avatar"
                alt="User Avatar"
              />
              <div
                v-else
                class="relative mr-2 inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-full
                  text-white"
                :class="avatarColor"
              >
                <span class="font-medium text-white">
                  {{ initials }}
                </span>
              </div>
            </button>
            <!-- Account Dropdown -->
            <div
              class="hs-dropdown-menu duration z-10 hidden w-60 rounded-xl bg-white opacity-0
                shadow-[0_10px_40px_10px_rgba(0,0,0,0.08)] transition-[opacity,margin] hs-dropdown-open:opacity-100"
              aria-labelledby="@@id"
            >
              <div class="border-b border-gray-200 p-1">
                <div
                  class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-gray-100
                    focus:bg-gray-100 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  href=""
                >
                  <img
                    v-if="user.avatar"
                    class="size-8 flex-shrink-0 rounded-full"
                    :src="user.avatar"
                    alt="User Avatar"
                  />
                  <div
                    v-else
                    class="relative mr-2 inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-full
                      text-white"
                    :class="avatarColor"
                  >
                    <span class="font-medium text-white">
                      {{ initials }}
                    </span>
                  </div>
                  <div class="grow">
                    <span class="text-sm font-semibold text-gray-800">
                      {{ user.first_name }} {{ user.last_name }}
                    </span>
                    <p class="text-xs text-gray-500">
                      {{ user.email }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="p-1">
                <a
                  class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-gray-100
                    focus:bg-gray-100 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  href="#"
                >
                  Sign out
                </a>
              </div>
            </div>
            <!-- End Account Dropdown -->
          </div>
          <!-- End Account Dropdown -->
        </div>
      </div>
    </div>
  </header>
</template>
